import type { BuiltInTool, BuiltInToolError, JsonSchema } from './types'

/**
 * #1827: a team's work list. Five tools over one service, callable from a run, from chat, from the API and from the
 * MCP server under the same rules: a run, a person, chat and an outside agent each add, list, read, change and close
 * items the same way.
 *
 * - A run is held to its teams, the teams its member sits on or leads (a task belongs to a role, not a team): it
 *   adds to and lists its first team, reaches items on any of them, and claims from each in turn. A `teamId` it
 *   passes is ignored, another team's item is `work_item_not_found`, and a run whose member sits on no team gets
 *   `no_team`. Chat, API and MCP callers pass `teamId`, held to their enterprise.
 * - Who added or changed an item is always the caller, never an argument.
 * - Every change is kept as the item's history; a closed item is read-only and keeps it.
 *
 * Each is served over its own `/v1/teams/:teamId/work-items` or `/v1/work-items/:id` route, under `teams:read` to
 * read and `teams:write` to change, as team KPI targets are.
 *
 * #1828 adds a sixth, `os.claim_work_item`, for runs only: it takes the next ready item and marks it as worked by
 * the calling run in one step, so two runs never take the same item.
 */

const OPEN_STATES = ['proposed', 'ready', 'held', 'in_progress'] as const
const ALL_STATES = ['proposed', 'ready', 'held', 'in_progress', 'done', 'dropped'] as const
const PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const
/**
 * What os.update_work_item takes to clear a priority. The served schemas have no null type, so a model or an MCP
 * client that checks its input could not send one; `WorkItemsService` reads this as null.
 */
const CLEAR_WORK_ITEM_PRIORITY = 'none'
/** #429: what os.list_work_items takes for every open state at once, the same set a list left without a state gives. */
const OPEN = 'open'
/** #429: what os.update_work_item takes to clear a parent, for the same reason as the priority's. */
const CLEAR_WORK_ITEM_PARENT = 'none'

const NO_TEAM: BuiltInToolError = {
    code: 'no_team',
    when: 'The member running the run sits on no team and leads none, so it has no list to keep.',
    handling: 'tool_error',
}

const TEAM_NOT_FOUND: BuiltInToolError = {
    code: 'team_not_found',
    when: "`teamId` names no team in this enterprise, another enterprise's included.",
    handling: 'tool_error',
}

const WORK_ITEM_NOT_FOUND: BuiltInToolError = {
    code: 'work_item_not_found',
    when: "`id` names no item this caller can reach: none at all, another enterprise's, or, for a run, a team it is not on.",
    handling: 'tool_error',
}

const WORK_ITEM_CLOSED: BuiltInToolError = {
    code: 'work_item_closed',
    when: 'The item is closed (`done` or `dropped`), so it can no longer be changed or closed the other way.',
    handling: 'tool_error',
}

const INVALID_WORK_ITEM: BuiltInToolError = {
    code: 'invalid_work_item',
    when:
        'A title, kind, state, priority, position, link, reason or forFunction is missing or not one the list accepts; or a ' +
        'parent is the item itself, has a parent of its own, is given to an item that has children, or is not an ' +
        'item on the same team.',
    handling: 'tool_error',
}

/** #1831: a proposed item waits for a person's approval, and a held one for a person's release. */
const APPROVAL_REQUIRES_PERSON: BuiltInToolError = {
    code: 'approval_requires_person',
    when: 'A run tries to move a proposed or held item to another state: only a person approves or releases one.',
    handling: 'tool_error',
}

/**
 * #671: one issue has one open item across the enterprise, whichever team's list it is on. The refusal names the
 * issue and nothing of the item holding it.
 */
const WORK_ITEM_EXISTS: BuiltInToolError = {
    code: 'work_item_exists',
    when: 'A link names an issue or pull request that another open item in the enterprise already holds, on any team’s list. The issue is taken: leave it and pick another. Closing that item frees it.',
    handling: 'tool_error',
}

const BODY_TOO_LARGE: BuiltInToolError = {
    code: 'body_too_large',
    when: 'The body is over 16 KB. Keep a larger document elsewhere and put its address in `links`.',
    handling: 'tool_error',
}

const ID: JsonSchema = {
    type: 'string',
    description: 'The item, by its id from os.list_work_items or os.add_work_item.',
}

const TEAM_ID: JsonSchema = {
    type: 'string',
    description:
        'The team whose list it is. A run leaves it out: the first team its member sits on or leads is used, and any other is ignored.',
}

/**
 * #431: the business functions a team can hold, as `BUSINESS_FUNCTIONS` in @zerohuman/db names them. A run names the
 * receiving team of an item by one of these, never by an id, so a task works in every enterprise it is cloned into.
 */
const BUSINESS_FUNCTIONS = ['support', 'marketing', 'sales', 'operations', 'engineering'] as const

const FOR_FUNCTION: JsonSchema = {
    type: 'string',
    description:
        "A run only: put the item on another team's list, the team of your enterprise that holds this business " +
        "function. It is added as proposed whatever state you give, for a person or that team's lead to approve: " +
        'you can never approve it, and unless you sit on that team you cannot read or change it afterwards. With ' +
        'no such team, or more than one, nothing is added: the finding goes to a person on Blockers and the answer ' +
        "says raised. Naming your first team's function adds the item to your first team's list as proposed. " +
        'Ignored outside a run, where teamId names the team.',
    enum: BUSINESS_FUNCTIONS,
}

const FOR_FUNCTION_RETURNS =
    'With forFunction, the answer is the item placed on the receiving team, with `origin` (the team and run that ' +
    'sent it), or, when no single team holds the function, `{ raised: true, reason, forFunction, candidates }`: ' +
    'nothing was added, a person was told on Blockers, and the run carries on.'

function itemFields(change: boolean): Record<string, JsonSchema> {
    return {
        title: {
            type: 'string',
            description: change ? 'The new title, up to 200 characters.' : 'What the work is, up to 200 characters.',
        },
        kind: {
            type: 'string',
            description:
                'A word the task chooses for the kind of work (content, outreach, bug): one word, no spaces, up to 40 characters.',
        },
        state: {
            type: 'string',
            description: change
                ? 'Move it between the open states: proposed waits for a person, ready can be claimed, held is parked until a person releases it. A run cannot move a proposed or held item out of that state. Use os.close_work_item to finish or drop it.'
                : 'Where it starts: proposed (waiting for a person) unless given. held is parked until a person releases it. Closed states are set by os.close_work_item.',
            enum: OPEN_STATES,
        },
        priority: {
            type: 'string',
            description: change
                ? 'Its level, urgent first. none clears it, so it lists after every level.'
                : 'Its level, urgent first. Left out, it has none and lists after every level.',
            enum: change ? [...PRIORITIES, CLEAR_WORK_ITEM_PRIORITY] : PRIORITIES,
        },
        ...(change
            ? {
                  position: {
                      type: 'integer',
                      description:
                          'Its place among items of the same level: lower lists first. A new item goes to the end.',
                  },
              }
            : {}),
        body: {
            type: 'string',
            description: change
                ? 'The new body: plain text, up to 16 KB.'
                : 'A short body: plain text, up to 16 KB. Link a larger document instead.',
        },
        links: {
            type: 'array',
            description: change
                ? 'The whole new list of links: up to 20 http(s) URLs.'
                : 'Up to 20 http(s) URLs the work refers to.',
            items: { type: 'string' },
        },
        parentId: {
            type: 'string',
            description: change
                ? `The item this one sits under, by its id: an item on the same team that has no parent itself. ${CLEAR_WORK_ITEM_PARENT} clears it. An item that has children takes no parent.`
                : 'The item this one sits under, such as a campaign or an epic, by its id: an item on the same team that has no parent itself. Left out, it stands alone.',
        },
    }
}

/**
 * #631: who an item is for. The OS sets it when the item is added, from whoever added it, and never from an
 * argument, so no work-item tool takes a requester.
 */
const RETURNS_REQUESTED_BY =
    '`requestedBy` (the person the work is for, `{ id, name }`, or null for nobody: set by the OS only when the ' +
    'item is added, from the person adding it or the person who asked for the run adding it, never from an argument)'

const RETURNS_ITEM =
    'The item: `id`, `teamId`, `title`, `kind`, `state`, `priority`, `position`, `body`, `links`, `addedBy`, ' +
    '`createdAt`, `updatedAt`, `closedAt`, `closeReason`, `claimedByRunId` and `claimedAt` (the run working it ' +
    `and since when, or null), \`parentId\` (the item it sits under, or null), and ${RETURNS_REQUESTED_BY}.`

const INVALID_CLAIM_KINDS: BuiltInToolError = {
    code: 'invalid_work_item',
    when: '`kinds` is not a list of 1 to 20 kinds, each one word with no spaces, up to 40 characters; `id` is not text; or both are given.',
    handling: 'tool_error',
}

/**
 * #1828: a run takes the next item of its teams' lists in one step. Run-scoped, as `os.ask_ceo` and
 * `os.stop_execution` are: a claim is held by a run, never a person, so chat and the API-token MCP server do not
 * offer it, and its endpoint is the run's own (`POST /v1/runs/self/work-items/claim`, `run:self`).
 */
const OS_CLAIM_WORK_ITEM: BuiltInTool = {
    name: 'os.claim_work_item',
    family: 'os.work',
    summary: "Take the next ready item of this run's team lists, so no other run can take it.",
    description:
        'Claim the highest-priority ready item on the lists of the teams you sit on or lead, your first team’s ' +
        'list first and the next team’s only when it has none, each in the order os.list_work_items gives ' +
        '(priority, then position, then age), optionally only of the kinds you give. Or give id to claim that one ' +
        'item, such as one you just added for the work you are handing on: it is taken if it is ready, or still in ' +
        'progress for a run that has ended; a held, proposed or closed item, or one a live run holds, answers ' +
        '{ item: null }. In the same step it moves to ' +
        'in_progress and is marked as worked by this run, so no other run can take it. A run holds one item: ' +
        'calling again answers with the item it already holds. When no item fits, the answer is { item: null }, ' +
        'not an error. The claim lasts while the run is live, gate waits included; if the run is cancelled without ' +
        'closing the item, it goes back to ready for the next run. If the run fails or is blocked, or its pipeline ' +
        'stops short, the item is held for a person, still naming the run: a retry takes it over, and clearing the ' +
        'run on Blockers or releasing the item puts it back to ready. When the run ends and hands ' +
        'off, every successor it starts receives the item as its workItem input and holds it in turn, so the runs ' +
        'on one item are one execution, its loop caps count laps on the item, and a task that declares its finish ' +
        'closes the item as done.',
    inputSchema: {
        type: 'object',
        properties: {
            kinds: {
                type: 'array',
                description:
                    'Only items of these kinds: 1 to 20, each one word with no spaces, up to 40 characters. Left out, any kind.',
                items: { type: 'string' },
                minItems: 1,
            },
            id: {
                type: 'string',
                description: 'Claim this one item of your teams’ lists instead of the next ready one. Not with kinds.',
            },
        },
        additionalProperties: false,
    },
    returns: `\`{ item }\`: the item this run now holds, with \`claimedByRunId\` and \`claimedAt\` set, or null when no ready item fits. ${RETURNS_ITEM}`,
    callers: ['run', 'api'],
    effect: 'write',
    retry: 'idempotent',
    retryNote:
        'The same tool call sent again answers with the first answer, and a run that already holds an item gets that item back: it never takes a second.',
    errors: [NO_TEAM, INVALID_CLAIM_KINDS, WORK_ITEM_NOT_FOUND],
    api: { method: 'POST', path: '/v1/runs/self/work-items/claim', scope: 'run:self' },
    examples: [
        { input: { kinds: ['content', 'outreach'] } },
        { input: {}, note: 'Any kind.' },
        { input: { id: '0192f7d4-5b1a-7c3e-9a2b-4c5d6e7f8a9b' }, note: 'The item you just added for an issue.' },
    ],
    public: true,
}

export const OS_WORK_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.add_work_item',
        family: 'os.work',
        summary: "Add an item to a team's work list.",
        description:
            "Add an item to a team's work list: a title, a kind (one word you choose), and optionally a state, a " +
            'priority, a short body (up to 16 KB), up to 20 links and a parent item on the same team (one level ' +
            'only). It starts proposed unless you give an open ' +
            'state, and goes to the end of its level. Who added it is recorded from the caller. A run adds to the first team its member sits on or leads, ' +
            "or, with forFunction, to the list of the team in its enterprise that holds that business function: as proposed, for that team's lead or a person to approve. " +
            'An issue or pull request has one open item across the enterprise: a link to one another open item holds, on any team’s list, is refused with work_item_exists, and the issue is taken.',
        inputSchema: {
            type: 'object',
            properties: { teamId: TEAM_ID, ...itemFields(false), forFunction: FOR_FUNCTION },
            required: ['title', 'kind'],
            additionalProperties: false,
        },
        returns: `${RETURNS_ITEM} ${FOR_FUNCTION_RETURNS}`,
        callers: ['run', 'chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same tool call sent again adds the item once and answers with the item it added.',
        errors: [NO_TEAM, TEAM_NOT_FOUND, INVALID_WORK_ITEM, BODY_TOO_LARGE, WORK_ITEM_EXISTS],
        api: { method: 'POST', path: '/v1/teams/:teamId/work-items', scope: 'teams:write' },
        examples: [
            {
                input: {
                    title: 'Draft the launch note',
                    kind: 'content',
                    priority: 'high',
                    links: ['https://example.com/brief'],
                },
            },
        ],
        public: true,
        chatPrompt: "os.add_work_item adds an item to a team's work list.",
    },
    {
        name: 'os.list_work_items',
        family: 'os.work',
        summary: "A team's work list, open items by default.",
        description:
            "List a team's work items: the open ones (proposed, ready, held, in_progress) unless you ask for a state, " +
            'optionally of one kind. Ordered by priority (urgent first, none last), then position, then age, in ' +
            'pages of 25 (up to 200). A run lists the first team its member sits on or leads. Each item carries its ' +
            'parentId and a rollup of its children, `children: { total, byState }`, closed children counted.',
        inputSchema: {
            type: 'object',
            properties: {
                teamId: TEAM_ID,
                state: {
                    type: 'string',
                    description: `Only items in this state, or ${OPEN} for every open state. Left out, every open state.`,
                    enum: [OPEN, ...ALL_STATES],
                },
                kind: { type: 'string', description: 'Only items of this kind.' },
                page: { type: 'integer', description: 'Which page, from 1.', minimum: 1 },
                limit: { type: 'integer', description: 'Items per page: 25 unless given.', minimum: 1, maximum: 200 },
            },
            additionalProperties: false,
        },
        returns:
            '`{ data, total, page, limit, pages }`: `data` is one page of items, each as os.read_work_item gives it, ' +
            '`claimedByRunStatus`, `children` and `requestedBy` included.',
        callers: ['run', 'chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [NO_TEAM, TEAM_NOT_FOUND, INVALID_WORK_ITEM],
        api: { method: 'GET', path: '/v1/teams/:teamId/work-items', scope: 'teams:read' },
        examples: [{ input: { state: 'ready', kind: 'content' } }],
        public: true,
        chatPrompt: "os.list_work_items lists a team's work items.",
    },
    {
        name: 'os.read_work_item',
        family: 'os.work',
        summary: 'One work item, with every field.',
        description:
            'Read one work item with every field, open or closed, and claimedByRunStatus: the status now of the run ' +
            'holding it (live, waiting at a gate, ended, or null for none), which tells work being done from work ' +
            'waiting, finished without closing the item, or stopped. Not found for an item in another enterprise, or, ' +
            'for a run, on the list of a team its member is not on.',
        inputSchema: {
            type: 'object',
            properties: { id: ID },
            required: ['id'],
            additionalProperties: false,
        },
        returns: `${RETURNS_ITEM} Also \`claimedByRunStatus\`: the status of the run holding it now, or null; and \`children\`: \`{ total, byState }\`, how many items sit under it and how many in each state, closed ones included.`,
        callers: ['run', 'chat', 'api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [NO_TEAM, WORK_ITEM_NOT_FOUND],
        api: { method: 'GET', path: '/v1/work-items/:id', scope: 'teams:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000031' } }],
        public: true,
        chatPrompt: 'os.read_work_item reads one work item.',
    },
    {
        name: 'os.update_work_item',
        family: 'os.work',
        summary: "Change an open work item's fields.",
        description:
            "Change an open item's title, kind, state (among the open ones), priority, position, body, links or parent. A " +
            'field left out keeps its value. Each change is kept in the item’s history with who made it, and a call ' +
            'that changes nothing records nothing. A closed item cannot be changed (work_item_closed). Only a person ' +
            'approves a proposed item or releases a held one: a run that tries is refused (approval_requires_person).',
        inputSchema: {
            type: 'object',
            properties: { id: ID, ...itemFields(true) },
            required: ['id'],
            additionalProperties: false,
        },
        returns: `The item after the change. ${RETURNS_ITEM}`,
        callers: ['run', 'chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same tool call sent again changes the item once and answers with the first answer.',
        errors: [
            NO_TEAM,
            WORK_ITEM_NOT_FOUND,
            WORK_ITEM_CLOSED,
            INVALID_WORK_ITEM,
            BODY_TOO_LARGE,
            APPROVAL_REQUIRES_PERSON,
            WORK_ITEM_EXISTS,
        ],
        api: { method: 'PATCH', path: '/v1/work-items/:id', scope: 'teams:write' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000031', state: 'in_progress' } }],
        public: true,
        chatPrompt: 'os.update_work_item changes an open work item.',
    },
    {
        name: 'os.close_work_item',
        family: 'os.work',
        summary: 'Close a work item as done or dropped.',
        description:
            'Close an item as done or dropped, with an optional reason. It stays readable with its whole history, ' +
            'and can no longer be changed. Closing it again the same way changes nothing; the other way is ' +
            'work_item_closed.',
        inputSchema: {
            type: 'object',
            properties: {
                id: ID,
                state: {
                    type: 'string',
                    description: 'done when the work is finished, dropped when it is not.',
                    enum: ['done', 'dropped'],
                },
                reason: { type: 'string', description: 'Why, in a sentence, up to 2000 characters.' },
            },
            required: ['id', 'state'],
            additionalProperties: false,
        },
        returns: `The item as closed, with \`closedAt\` set. ${RETURNS_ITEM}`,
        callers: ['run', 'chat', 'api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'Closing an item again in the same state changes nothing and answers with the item as closed.',
        errors: [NO_TEAM, WORK_ITEM_NOT_FOUND, WORK_ITEM_CLOSED, INVALID_WORK_ITEM],
        api: { method: 'POST', path: '/v1/work-items/:id/close', scope: 'teams:write' },
        examples: [
            { input: { id: '5f0c1d2e-0000-4000-8000-000000000031', state: 'done', reason: 'Published on Friday' } },
        ],
        public: true,
        chatPrompt: 'os.close_work_item closes a work item as done or dropped.',
    },
    // #1828: after the five chat and the MCP server list, so neither list moves.
    OS_CLAIM_WORK_ITEM,
]
